/**
 * Formats a date string to Spanish (Argentina) locale format
 * Handles dates without time (YYYY-MM-DD) and dates with time (YYYY-MM-DD HH:mm:ss)
 * Avoids timezone issues by parsing dates manually
 * 
 * @param {string} dateString - Date string in format "YYYY-MM-DD" or "YYYY-MM-DD HH:mm:ss"
 * @returns {string} Formatted date in format "DD/MM/YYYY"
 */
export const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // Check if date includes time
    const hasTime = dateString.includes(' ') || dateString.includes('T');
    
    if (hasTime) {
        // For dates with time, parse manually to avoid timezone issues
        // Format: "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DDTHH:mm:ss"
        const dateTimeParts = dateString.split(/[\sT]/);
        if (dateTimeParts.length < 2) {
            return '';
        }
        
        const datePart = dateTimeParts[0];
        const parts = datePart.split('-');
        if (parts.length !== 3) {
            return '';
        }
        
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in Date
        const day = parseInt(parts[2], 10);
        
        // Parse time if available
        const timePart = dateTimeParts[1];
        let hours = 0, minutes = 0, seconds = 0;
        if (timePart) {
            const timeParts = timePart.split(':');
            hours = parseInt(timeParts[0] || 0, 10);
            minutes = parseInt(timeParts[1] || 0, 10);
            seconds = parseInt(timeParts[2] || 0, 10);
        }
        
        // Create date in local timezone
        const date = new Date(year, month, day, hours, minutes, seconds);
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
            return '';
        }
        
        return date.toLocaleDateString('es-AR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    } else {
        // For dates without time (YYYY-MM-DD), parse manually to avoid timezone issues
        const parts = dateString.split('-');
        if (parts.length !== 3) {
            return '';
        }
        
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in Date
        const day = parseInt(parts[2], 10);
        
        // Create date in local timezone
        const date = new Date(year, month, day);
        
        // Format to Spanish (Argentina) locale
        return date.toLocaleDateString('es-AR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    }
};

/**
 * Formats a date string with time to Spanish (Argentina) locale format
 * Avoids timezone issues by parsing dates manually
 * 
 * @param {string} dateString - Date string in format "YYYY-MM-DD HH:mm:ss"
 * @returns {string} Formatted date in format "DD/MM/YYYY HH:mm"
 */
export const formatDateTime = (dateString) => {
    if (!dateString) return '';
    
    // Parse manually to avoid timezone issues
    // Format: "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DDTHH:mm:ss"
    const dateTimeParts = dateString.split(/[\sT]/);
    if (dateTimeParts.length < 2) {
        // If no time, just return formatted date
        return formatDate(dateString);
    }
    
    const datePart = dateTimeParts[0];
    const parts = datePart.split('-');
    if (parts.length !== 3) {
        return '';
    }
    
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in Date
    const day = parseInt(parts[2], 10);
    
    // Parse time
    const timePart = dateTimeParts[1];
    let hours = 0, minutes = 0, seconds = 0;
    if (timePart) {
        const timeParts = timePart.split(':');
        hours = parseInt(timeParts[0] || 0, 10);
        minutes = parseInt(timeParts[1] || 0, 10);
        seconds = parseInt(timeParts[2] || 0, 10);
    }
    
    // Create date in local timezone
    const date = new Date(year, month, day, hours, minutes, seconds);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
        return '';
    }
    
    // Format date and time separately to have full control
    const formattedDate = date.toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
    
    const formattedTime = date.toLocaleTimeString('es-AR', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
    
    return `${formattedDate} ${formattedTime}`;
};

